import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { Box, Typography, Checkbox } from '@material-ui/core';
import { lock, unlock } from '../../features/focus-lock/focusLockSlice';
import QCXDialog from './QCXDialog';
import QCXDialogTitle from './QCXDialogTitle';
import QCXDialogContent from './QCXDialogContent';
import QCXDialogActions from './QCXDialogActions';
import QCXButton from '../button/QCXButton';
import i18n from '../../i18n';

const DEFAULT_BUTTON_GROUP_OPTIONS = {
  confirm: {
    variant: 'contained',
    color: 'secondary',
    description: i18n.t('com.muralis.qcx.acoes.confirmar'),
  },
  cancel: {
    variant: 'outlined',
    color: 'default',
    description: i18n.t('com.muralis.qcx.acoes.cancelar'),
  },
};

export default function QCXConfirmDialogNotaDebito({
  open,
  title,
  despesas = [],          
  selecionadas = {},      
  loading = false,
  onToggle,               
  onSelectAll,            
  onClearSelection,       
  onConfirm,
  onClose,
  onSecondaryConfirm = undefined,
  buttonSize = 'small',
  buttonGroupOptions = DEFAULT_BUTTON_GROUP_OPTIONS,
  formatValor = (v) => v, 
  disableBackdropClick = true,
  disableRestoreFocus = true,
  disableEnforceFocus = true,
  ...restProps
}) {
  const dispatch = useDispatch();

  const buttonGroupOptionsMounted = useMemo(
    () => ({
      ...DEFAULT_BUTTON_GROUP_OPTIONS,
      ...buttonGroupOptions,
      confirm: {
        ...DEFAULT_BUTTON_GROUP_OPTIONS?.confirm,
        ...buttonGroupOptions?.confirm,
      },
      secondaryConfirm: {
        ...DEFAULT_BUTTON_GROUP_OPTIONS?.secondaryConfirm,
        ...buttonGroupOptions?.secondaryConfirm,
      },
      cancel: {
        ...DEFAULT_BUTTON_GROUP_OPTIONS?.cancel,
        ...buttonGroupOptions?.cancel,
      },
    }),
    [buttonGroupOptions]
  );

  useEffect(() => {
    if (open) {
      dispatch(unlock());
    }
  }, [open]);

  const handleInternalClose = useCallback(
    (...args) => {
      onClose?.(...args);
      dispatch(lock());
    },
    [onClose]
  );

  const hasAnySelected = useMemo(
    () => Object.values(selecionadas || {}).some((v) => v === true),
    [selecionadas]
  );

  return (
    <QCXDialog
      key="modal-confirm-dialog-nota-debito"
      id="modal-confirm-dialog-nota-debito"
      open={open}
      onClose={handleInternalClose}
      aria-labelledby="nota-debito-dialog-title"
      aria-describedby="nota-debito-dialog-description"
      disableBackdropClick={disableBackdropClick}
      disableRestoreFocus={disableRestoreFocus}
      disableEnforceFocus={disableEnforceFocus}
      maxWidth="sm"
      fullWidth
      {...restProps}
    >
      {title && (
        <QCXDialogTitle id="nota-debito-dialog-title">
          {title}
        </QCXDialogTitle>
      )}

      <QCXDialogContent>
        {loading && (
          <Typography variant="body2">
            Carregando despesas...
          </Typography>
        )}

        {!loading && despesas.length === 0 && (
          <Typography variant="body2">
            Nenhuma despesa elegível encontrada para este faturamento.
          </Typography>
        )}

        {!loading && despesas.length > 0 && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={1} mr={-2.5}>
              <QCXButton
                size="small"
                variant="outlined"
                color="primary"
                onClick={onClearSelection}
              >
                Limpar seleção
              </QCXButton>
              <QCXButton
                size="small"
                variant="outlined"
                color="secondary"
                onClick={onSelectAll}
              >
                Selecionar todas
              </QCXButton>
            </Box>

            {despesas.map((despesa) => {
              const chave = `${despesa.origem}:${despesa.id}`;
              const checked = !!selecionadas[chave];

              return (
                <Box
                  key={chave}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                  mr={-1.5}
                >
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={checked}
                      onChange={() => onToggle(despesa.origem, despesa.id)}
                      color="primary"
                      size="small"
                    />
                    <Typography variant="body2">
                      {despesa.descricao}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {formatValor(despesa.valor)}
                  </Typography>
                </Box>
              );
            })}
          </>
        )}
      </QCXDialogContent>

      <QCXDialogActions>
        <QCXButton
          variant="outlined"
          onClick={handleInternalClose}
          color="default"
          size={buttonSize}
          {...buttonGroupOptionsMounted?.cancel}
        >
          {buttonGroupOptionsMounted?.cancel?.description ||
            i18n.t('com.muralis.qcx.acoes.cancelar')}
        </QCXButton>

        {onSecondaryConfirm && (
          <QCXButton
            variant="outlined"
            onClick={onSecondaryConfirm}
            color="primary"
            size={buttonSize}
            {...buttonGroupOptionsMounted?.secondaryConfirm}
          >
            {buttonGroupOptionsMounted?.secondaryConfirm?.description || ''}
          </QCXButton>
        )}

        <QCXButton
          variant="contained"
          onClick={onConfirm}
          color="secondary"
          size={buttonSize}
          autoFocus
          disabled={!hasAnySelected}
          {...buttonGroupOptionsMounted?.confirm}
        >
          {buttonGroupOptionsMounted?.confirm?.description ||
            i18n.t('com.muralis.qcx.acoes.confirmar')}
        </QCXButton>
      </QCXDialogActions>
    </QCXDialog>
  );
}
